import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Divider,
  Flex,
  Grid,
  Heading,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  Text,
} from '@chakra-ui/react';
import { CargoType, DocumentType, FreightService, FreightServiceAddOn } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { ConditionalField, confirmationOptions, DOCK_HOURS_OPTIONS } from '@pelicargo/utils';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { HiOutlineExclamation } from 'react-icons/hi';

import { API } from '../../utils/api-typings';
import { SimpleDatePickerInput } from '../DatePicker/SimpleDatePickerInput';
import { SimpleDropzone } from '../SimpleDropzone';
import { SimpleInput } from '../SimpleInput';
import { SimpleSelect } from '../SimpleSelect';
import { SimpleSwitch } from '../SimpleSwitch';
import { SimpleTextarea } from '../SimpleTextarea';
import { ConditionalFields } from './ConditionalFieldsForm';
import { PickupAddressForm } from './PickupAddressForm';

type PaperworkYesNoProps = {
  name: string;
  label: string;
};

const PaperworkYesNo = ({ name, label }: PaperworkYesNoProps) => (
  <Stack borderWidth="1px" borderColor="border.light" borderRadius="md" px="3" py="2.5" spacing="2" justify="center">
    <SimpleSwitch
      name={name}
      label={label}
      options={confirmationOptions}
      direction="row"
      variant="outline"
      fieldVariant="unstyled"
      size="sm"
    />
  </Stack>
);

type Props = {
  quote: API.GetQuote;
  conditionalFields: ConditionalField[];
};

export const InlandBookingForm = ({ quote, conditionalFields }: Props) => {
  const methods = useFormContext();

  const requestId = quote?.request_id;
  const request = quote?.request;
  const expiresAt = quote?.expires_at;

  const documentsInForm = methods.watch('documents');

  const isDangerousGoods = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);
  const shouldShowDgdWarning = useMemo(() => {
    const hasDocument = documentsInForm?.length > 0;

    if (isDangerousGoods && !hasDocument) return true;
    return false;
  }, [documentsInForm, isDangerousGoods]);

  const isExpired = useMemo(() => {
    if (!expiresAt) return false;

    const convertToTZ = (date: Date, timeZone: string) => {
      const newDate = new Date(date.toLocaleString('en-US', { timeZone }));
      return newDate;
    };
    const originTimeZone = quote?.origin_airport?.time_zone;
    const adjustedExpiresAt = convertToTZ(expiresAt, originTimeZone);
    adjustedExpiresAt.setHours(23, 59, 59, 999);
    const now = convertToTZ(new Date(), originTimeZone);

    return adjustedExpiresAt < now;
  }, [expiresAt, quote?.origin_airport?.time_zone]);

  const isFCA = useMemo(
    () => quote?.request?.freight_services?.includes(FreightService.FCA),
    [quote?.request?.freight_services],
  );

  // "Will there be HAWBs?" now lives in Paperwork, so don't also render it as a
  // provider-conditional field.
  const visibleConditionalFields = useMemo(
    () => conditionalFields?.filter((field) => field !== ConditionalField.IS_CONSOLIDATED) ?? [],
    [conditionalFields],
  );

  // AES is the only freight service add-on, so present it as a Yes/No backed by
  // a proxy field that we keep in sync with `freight_service_add_ons`. The
  // toggle starts unanswered (no default selection); we only mirror the answer
  // into `freight_service_add_ons` once the user picks Yes/No.
  const aesRequested = methods.watch('aesRequested');

  useEffect(() => {
    if (aesRequested === undefined) return;
    const current: string[] = methods.getValues('freight_service_add_ons') || [];
    const hasAes = current.includes(FreightServiceAddOn.AES);
    if (hasAes === !!aesRequested) return;
    methods.setValue('freight_service_add_ons', aesRequested ? [FreightServiceAddOn.AES] : [], { shouldDirty: true });
  }, [aesRequested, methods]);

  return (
    <Stack w="full" spacing="3">
      <Stack spacing="5" divider={<Divider />}>
        {/* Using this instead of <Show/> so <Divider/> doesn't render */}
        {!isFCA && (
          <Stack spacing="8">
            <Stack>
              <Heading variant="h5">Pickup</Heading>
              <Heading fontSize="sm" textTransform="uppercase">
                Details
              </Heading>
              <Stack>
                <Stack direction={{ base: 'column', sm: 'row' }} w="full">
                  <Stack direction="row" w="full">
                    <SimpleDatePickerInput
                      name="pickup_ready_at"
                      label="Ready Date *"
                      variant="outline"
                      iconFontSize="20px"
                      placeholder="Ready Date"
                      disablePastDates
                    />
                  </Stack>
                  <Stack direction="row" align="center" w="full" spacing="1">
                    <SimpleSelect
                      name="from_dock_hours"
                      label="Dock Hours *"
                      options={DOCK_HOURS_OPTIONS}
                      variant="outline"
                    />
                    <Text>-</Text>
                    <SimpleSelect
                      name="to_dock_hours"
                      label="Dock Hours *"
                      options={DOCK_HOURS_OPTIONS}
                      variant="outline"
                    />
                  </Stack>
                </Stack>
                <PickupAddressForm />
                <SimpleInput
                  name="pickup_ref"
                  label="Pickup Reference #"
                  placeholder="x x x   x x x   x x x"
                  variant="outline"
                />
              </Stack>
            </Stack>
            <Stack>
              <Heading fontSize="sm" textTransform="uppercase">
                Contact
              </Heading>
              <Stack direction="row" w="full">
                <SimpleInput
                  name="inland_dropoff_contact.full_name"
                  label="Full Name *"
                  placeholder="Full Name"
                  flex="1"
                />
                <SimpleInput name="inland_dropoff_contact.phone_number" label="Phone *" placeholder="Phone" flex="1" />
                <SimpleInput
                  name="inland_dropoff_contact.email"
                  label="Email (Optional)"
                  placeholder="Email"
                  flex="1"
                />
              </Stack>
            </Stack>
          </Stack>
        )}
        {isFCA && (
          <Stack>
            <Heading variant="h5">Drop-off</Heading>
            <SimpleDatePickerInput
              name="dropoff_at"
              label="Drop-off date *"
              variant="outline"
              iconFontSize="20px"
              placeholder="Drop-off date"
              disablePastDates
            />
            <SimpleInput
              name="trucking_name"
              label="Trucking Company (Optional)"
              placeholder="Trucking Company"
              flex="1"
              variant="outline"
            />
          </Stack>
        )}
        <Stack spacing="4">
          <Stack spacing="1">
            <Heading variant="h5">Paperwork</Heading>
            <Text color="text.subtle" fontSize="sm">
              Let us know your filing and AWB preferences so we can prepare the right paperwork for this shipment.
            </Text>
          </Stack>

          <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="3">
            <PaperworkYesNo name="aesRequested" label="Do you need AES Filing?" />
            <PaperworkYesNo name="is_consolidated" label="Will there be HAWBs?" />
          </Grid>

          <PaperworkYesNo name="awb_instructions_ready" label="Are your AWB instructions ready?" />
        </Stack>
        {visibleConditionalFields.length > 0 && <ConditionalFields quote={quote} fields={visibleConditionalFields} />}
        <Stack spacing="6">
          <Stack>
            <Heading variant="h5">Additional Information (Optional)</Heading>
            <SimpleInput
              label="Your Reference #"
              name="customer_ref"
              placeholder="x x x   x x x   x x x"
              variant="outline"
              flex="1"
            />
          </Stack>
          <SimpleTextarea name="additional_information" label="Notes" placeholder="Notes" variant="outline" rows={2} />
        </Stack>
      </Stack>

      <Show if={shouldShowDgdWarning}>
        <Flex>
          <Tag size="lg" borderRadius="full" variant="outline" colorScheme="red" py="2">
            <TagLeftIcon as={HiOutlineExclamation} boxSize="6" />
            <TagLabel fontSize="lg">Missing Dangerous Goods Declaration - Upload to Book</TagLabel>
          </Tag>
        </Flex>
      </Show>
      <Show if={isDangerousGoods}>
        <SimpleDropzone name="documents" documentType={DocumentType.DGD} requestId={requestId} />
      </Show>
      <Show if={isExpired}>
        <Flex>
          <Alert status="error" variant="subtle" fontSize="sm" borderRadius="md">
            <AlertIcon />
            <Stack spacing="1">
              <AlertTitle>Quote Expired</AlertTitle>
              <AlertDescription>
                This quote has passed its expiry date. Booking this may not result in the same price.
              </AlertDescription>
            </Stack>
          </Alert>
        </Flex>
      </Show>
    </Stack>
  );
};
